import { Tooltip } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";

import {
  IFitbitSleepStageSummary,
  IFitbitSleepStats,
} from "../../../../../../@types/Fitbit";
import { renderChartEmptyState } from "../../../../../../components/ChartEmptyState";
import { CustomModalChartTick } from "../../../../../../components/ModalChart/CustomModalChartTick/CustomModalChartTick";
import {
  getEmptyAllAxis,
  getGraphSeparators,
} from "../../../../../../utils/chartUtils";
import { formatHoursAndMinutes } from "../../../../../../utils/dateFormatter";
import { DATE_FILTER_ALL, DateFilterOptionType } from "../CustomOptionsBar";
import { CustomFitbitSleepLegend } from "./CustomFitbitSleepLegend";
import { ChartWrapper, TooltipCss } from "./FitbitSleepChart.styles";

export type FitbitSleepChartRow = {
  timeValue: number;
  hoursAsleep: number;
  minutesAsleep: number;
  sessionId?: string;
  provider?: string;
  stageSummary: IFitbitSleepStageSummary | null;
};

const BAR_COLOR = "#54A6AA";

type FitbitSleepChartProps = {
  data: FitbitSleepChartRow[];
  width: string | number;
  height: string | number;
  isPinnedGraph?: boolean;
  // When the pinned "Selected session" side panel is open the chart shares the
  // tile with it, so it must shrink to the leftover space instead of keeping
  // its 658px floor (which would overflow the panel on smaller screens).
  hasSidePanel?: boolean;
  dateInterval: DateFilterOptionType;
  allRange?: { startDate: Dayjs; endDate: Dayjs } | null;
  statistics?: IFitbitSleepStats;
  onlyGraph?: boolean;
  isEmpty?: boolean;
  providerLabel?: string | null;
  selectedSessionId?: string;
  onSelectSession?: (row: FitbitSleepChartRow) => void;
};

export function FitbitSleepChart({
  data,
  width,
  height,
  dateInterval,
  allRange,
  statistics,
  isPinnedGraph,
  hasSidePanel,
  onlyGraph,
  isEmpty,
  providerLabel,
  selectedSessionId,
  onSelectSession,
}: FitbitSleepChartProps) {
  const { t } = useTranslation();

  const minDate =
    data.length > 0
      ? dayjs(Math.min(...data.map((d) => d.timeValue)))
      : undefined;
  const maxDate =
    data.length > 0
      ? dayjs(Math.max(...data.map((d) => d.timeValue)))
      : undefined;

  const dataPointTicks = useMemo(() => data.map((d) => d.timeValue), [data]);

  const isAllView = dateInterval.id === DATE_FILTER_ALL;
  const allViewStart = isAllView ? allRange?.startDate : undefined;
  const allViewEnd = isAllView ? allRange?.endDate : undefined;

  const emptyAllAxis = getEmptyAllAxis(dateInterval, isEmpty);

  const graphSeparatorResult = getGraphSeparators(
    dateInterval,
    emptyAllAxis?.anchor ??
      (isEmpty ? dateInterval.startDate : allViewStart ?? minDate),
    emptyAllAxis?.anchor ??
      (isEmpty ? dateInterval.endDate : allViewEnd ?? maxDate)
  );

  const {
    graphSeparators,
    dates,
    separatorCounter,
    viewType,
    isTickMultipleLine,
    interval,
  } = graphSeparatorResult;

  const isSleepDataPointMode = interval === "48H" || interval === "24H";

  const tickFormat = useMemo(() => {
    if (interval === "48H" || interval === "24H") return "MMM D";
    return graphSeparatorResult.tickFormat;
  }, [interval, graphSeparatorResult.tickFormat]);

  const xAxisTicks = useMemo(() => {
    if (isSleepDataPointMode) return dataPointTicks;
    if (viewType === "yearly") return graphSeparators;
    return dates;
  }, [isSleepDataPointMode, dataPointTicks, viewType, graphSeparators, dates]);

  const refs = useMemo<number[]>(() => {
    if (isSleepDataPointMode) return dataPointTicks;
    return [
      ...graphSeparators,
      ...dates.filter((_, idx) => idx % separatorCounter === 0),
    ];
  }, [
    isSleepDataPointMode,
    dataPointTicks,
    graphSeparators,
    dates,
    separatorCounter,
  ]);

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipTitle, setTooltipTitle] = useState<React.ReactNode>(null);
  const [pos, setPos] = useState<{ left: number; top: number }>({
    left: 0,
    top: 0,
  });
  const [hoveredBarIdx, setHoveredBarIdx] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const chartCardAllView = onlyGraph && isAllView;

  const tickXFormatter = (value: string) => {
    if (!isEmpty && (!data || data.length === 0)) return "";

    const numValue = Number(value);

    if (isSleepDataPointMode) {
      if (dataPointTicks.includes(numValue)) {
        return dayjs(numValue).format(tickFormat);
      }
      return "";
    }

    if (graphSeparators.includes(numValue) || refs.includes(numValue)) {
      if (tickFormat === "Q") {
        return `Q${dayjs(value).format(tickFormat)}, ${dayjs(value).format(
          "YY"
        )}`;
      }
      return dayjs(value).format(tickFormat);
    }

    return "";
  };

  const handleXDomain = (): [number, number] => {
    if (!dateInterval) return [0, 1];
    if (!isEmpty && (!data || data.length === 0)) return [0, 1];

    if (emptyAllAxis) return emptyAllAxis.domain;

    if (isAllView) {
      const start = allViewStart ?? minDate;
      const end = allViewEnd ?? maxDate;
      if (start && end) {
        const padding = 15 * 24 * 60 * 60 * 1000;
        return [start.valueOf() - padding, end.valueOf() + padding];
      }
    }

    let { startDate, endDate } = dateInterval;

    if (interval === "48H" || interval === "24H") {
      const padding = 12 * 60 * 60 * 1000;
      return [
        startDate.startOf("day").valueOf() - padding,
        endDate.endOf("day").valueOf() + padding,
      ];
    }

    if (interval === "7D" || interval === "2M") {
      startDate = startDate.add(-12, "hour");
    }

    if (minDate && maxDate && endDate.diff(startDate, "year") > 5) {
      startDate = minDate;
      endDate = maxDate;
    }

    if (endDate.diff(startDate, "months") > 11) {
      endDate = endDate.add(30, "day");
    }

    const needsPadding =
      interval === "7D" ||
      interval === "1M" ||
      interval === "2M" ||
      interval === "6M" ||
      interval === "1Y" ||
      interval === "4Y" ||
      interval === "4Y+";

    if (needsPadding) {
      const padding = 12 * 60 * 60 * 1000;
      return [startDate.valueOf() - padding, endDate.valueOf() + padding];
    }

    return [startDate.valueOf(), endDate.valueOf()];
  };

  const handleMouseMove = (barData: any, idx: number, e: React.MouseEvent) => {
    const { clientX, clientY } = e;

    setPos((prev) =>
      prev.left === clientX && prev.top === clientY
        ? prev
        : { left: clientX, top: clientY }
    );

    setHoveredBarIdx(idx);

    const payload = barData?.payload as FitbitSleepChartRow | undefined;
    if (!payload) return;

    const mutedText: React.CSSProperties = { fontSize: 12, color: "#D6D6D6" };
    const dateLabel = dayjs(payload.timeValue).format("MMM D, YYYY");
    const stages = payload.stageSummary;
    const minutesToHours = (m?: number | null) =>
      m && m > 0 ? m / 60 : undefined;

    const title = (
      <div className={TooltipCss()} style={{ alignItems: "flex-start" }}>
        <p style={{ fontWeight: 600, marginBottom: 4 }}>{dateLabel}</p>
        <p style={{ ...mutedText, fontWeight: 500 }}>
          {t("patientPage_measurementModal_sleep_stageDuration", {
            stage: t("patientPage_measurementModal_sleep_total"),
            duration: formatHoursAndMinutes(payload.hoursAsleep),
          })}
        </p>
        {minutesToHours(stages?.deepMinutes) !== undefined && (
          <p style={mutedText}>
            {t("patientPage_measurementModal_sleep_stageDuration", {
              stage: t("patientPage_measurementModal_sleep_deep"),
              duration: formatHoursAndMinutes(
                minutesToHours(stages?.deepMinutes) ?? 0
              ),
            })}
          </p>
        )}
        {minutesToHours(stages?.lightMinutes) !== undefined && (
          <p style={mutedText}>
            {t("patientPage_measurementModal_sleep_stageDuration", {
              stage: t("patientPage_measurementModal_sleep_light"),
              duration: formatHoursAndMinutes(
                minutesToHours(stages?.lightMinutes) ?? 0
              ),
            })}
          </p>
        )}
        {minutesToHours(stages?.remMinutes) !== undefined && (
          <p style={mutedText}>
            {t("patientPage_measurementModal_sleep_stageDuration", {
              stage: t("patientPage_measurementModal_sleep_rem"),
              duration: formatHoursAndMinutes(
                minutesToHours(stages?.remMinutes) ?? 0
              ),
            })}
          </p>
        )}
        {minutesToHours(stages?.awakeMinutes) !== undefined && (
          <p style={mutedText}>
            {t("patientPage_measurementModal_sleep_stageDuration", {
              stage: t("patientPage_measurementModal_sleep_awake"),
              duration: formatHoursAndMinutes(
                minutesToHours(stages?.awakeMinutes) ?? 0
              ),
            })}
          </p>
        )}
      </div>
    );

    setTooltipTitle(title);
    setTooltipOpen(true);
  };

  const handleMouseLeave = () => {
    setTooltipOpen(false);
    setHoveredBarIdx(null);
  };

  const handleBarClick = (barData: any) => {
    const payload = barData?.payload;
    if (payload && onSelectSession) {
      onSelectSession(payload as FitbitSleepChartRow);
    }
  };

  const maxYDomain = useMemo(() => {
    if (!data || data.length === 0) return 12;
    const maxTotal = Math.max(...data.map((d) => d.hoursAsleep));
    return Math.min(24, Math.ceil(maxTotal + 1));
  }, [data]);

  return (
    <ChartWrapper
      ref={containerRef}
      css={{ minWidth: isEmpty ? 600 : undefined }}
    >
      <Tooltip
        open={tooltipOpen}
        title={tooltipTitle}
        placement="top"
        zIndex={3000}
        arrow={false}
        styles={{
          body: {
            padding: "10px 20px",
            minHeight: "40px",
            width: "max-content",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "'Open Sans', sans-serif",
            fontSize: "12px !important",
            fontWeight: "500",
            backgroundColor: "#4b4b4b",
          },
        }}
        mouseEnterDelay={0.1}
      >
        <span
          style={{
            position: "fixed",
            left: pos.left,
            top: pos.top,
            width: 1,
            height: 1,
          }}
        />
      </Tooltip>

      <ResponsiveContainer
        debounce={10}
        width={width}
        height={height}
        minWidth={isPinnedGraph && !hasSidePanel ? 658 : undefined}
      >
        <BarChart
          style={{
            width: "100%",
            maxHeight: "70vh",
            cursor: onSelectSession ? "pointer" : "default",
          }}
          data={data}
          margin={
            onlyGraph
              ? { top: 5, right: 5, left: 5, bottom: 5 }
              : { top: 5, right: 32, left: 5, bottom: 5 }
          }
        >
          {!onlyGraph ? (
            <CartesianGrid strokeDasharray="2" vertical={false} />
          ) : null}

          <XAxis
            tickMargin={14}
            stroke="#D8D8D8"
            type={chartCardAllView ? "category" : "number"}
            scale="time"
            ticks={xAxisTicks}
            domain={
              chartCardAllView
                ? [
                    (minDate?.valueOf() ?? 0) - 15 * 24 * 60 * 60 * 1000,
                    (maxDate?.valueOf() ?? 0) + 15 * 24 * 60 * 60 * 1000,
                  ]
                : handleXDomain()
            }
            tickFormatter={tickXFormatter}
            allowDuplicatedCategory={false}
            tickLine={false}
            height={refs.length >= 6 ? 70 : 48}
            minTickGap={refs.length >= 6 ? 20 : 100}
            interval={0}
            hide={onlyGraph}
            dataKey="timeValue"
            tick={
              isTickMultipleLine && !isSleepDataPointMode ? (
                <CustomModalChartTick />
              ) : (
                {
                  fill: isEmpty ? "#B5B5B6" : "#727272",
                  fontSize: "12px",
                  fontWeight: "400",
                }
              )
            }
            angle={refs.length >= 6 ? -45 : 0}
          />

          <YAxis
            label={
              !onlyGraph
                ? {
                    value: t("patientPage_measurementModal_hoursAxis"),
                    angle: -90,
                    position: "insideLeft",
                    dx: -5,
                    dy: 20,
                    style: {
                      fontSize: "12px",
                      color: "#727272CC",
                      marginLeft: 12,
                    },
                  }
                : undefined
            }
            tickMargin={8}
            stroke="#D8D8D8"
            domain={[0, maxYDomain]}
            tickCount={Math.min(maxYDomain + 1, 13)}
            width={48}
            hide={onlyGraph}
            tick={{
              fill: isEmpty ? "#B5B5B6" : "#727272",
              fontSize: "12px",
              fontWeight: 400,
            }}
            allowDuplicatedCategory={false}
            allowDecimals={false}
          />

          {!onlyGraph
            ? refs.map((ref) => (
                <ReferenceLine
                  key={ref.toString()}
                  x={ref}
                  stroke="#D8D8D8"
                  strokeDasharray="8 1000"
                />
              ))
            : null}

          {!onlyGraph ? (
            <Legend
              content={
                <CustomFitbitSleepLegend
                  data={data}
                  statistics={statistics}
                  startDate={
                    isAllView && (allViewStart ?? minDate)
                      ? allViewStart ?? minDate!
                      : dateInterval.startDate
                  }
                  endDate={
                    isAllView && (allViewEnd ?? maxDate)
                      ? allViewEnd ?? maxDate!
                      : dateInterval.endDate
                  }
                  providerLabel={providerLabel}
                />
              }
            />
          ) : null}

          <Bar
            dataKey="hoursAsleep"
            fill={BAR_COLOR}
            radius={[4, 4, 0, 0]}
            isAnimationActive={false}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onClick={handleBarClick}
            barSize={onlyGraph ? 6 : 18}
            shape={(props: any) => {
              const isSelected =
                selectedSessionId &&
                props.payload?.sessionId === selectedSessionId;
              const isHovered = hoveredBarIdx === props.index;
              let opacity = selectedSessionId && !isSelected ? 0.45 : 1;
              if (isHovered) opacity *= 0.8;
              const { x, y, width, height } = props;
              // Round only the top corners, keep the bottom flat (at the axis).
              const radius = Math.max(0, Math.min(4, width / 2, height));
              const path = `M${x},${y + height}
                L${x},${y + radius}
                Q${x},${y} ${x + radius},${y}
                L${x + width - radius},${y}
                Q${x + width},${y} ${x + width},${y + radius}
                L${x + width},${y + height}
                Z`;
              return <path d={path} fill={props.fill} opacity={opacity} />;
            }}
          />

          {isEmpty &&
            renderChartEmptyState({
              message: t("patientPage_measurementModal_sleep_empty"),
            })}
        </BarChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
}
